/**
 * Character Card Component
 *
 * Displays a single rigged character in the library grid.
 */

import React from 'react';
import { CharacterRig } from '../../types/RiggingTypes';
import {
  isRigComplete,
  getCharacterVoiceAsset,
} from '../../services/riggedCharacterService';
import { isCharacterRiggingEnabled } from '../../services/featureFlagsService';
import { useCharacterImageUrl } from '../../services/characterStorageService';
import {
  Edit,
  Delete,
  Add,
  PlayArrow,
  FiberManualRecord,
  GraphicEq,
} from '@mui/icons-material';

interface CharacterCardProps {
  character: CharacterRig;
  /** Opens the character details editor (identity, voice, image). */
  onEditDetails: () => void;
  /** Opens the rig editor (only wired when rigging is enabled). */
  onEdit: () => void;
  onDelete: () => void;
  onAddToCanvas: () => void;
  onApplyAction: () => void;
  onRecordAnimation?: () => void;
  onVoice?: () => void;
}

const CharacterCard: React.FC<CharacterCardProps> = ({
  character,
  onEditDetails,
  onEdit,
  onDelete,
  onAddToCanvas,
  onApplyAction,
  onRecordAnimation,
  onVoice,
}) => {
  const rigComplete = isRigComplete(character);
  const riggingEnabled = isCharacterRiggingEnabled();
  const displayCharacterName =
    character.name || character.identity?.name || character.imageName;
  // The stored imageUrl may be a relative blob path in the user's storage
  // directory; sign it with the user SAS token for display.
  const imageSrc = useCharacterImageUrl(character.imageUrl);
  const hasVoice = !!character.voiceUrl;
  const voiceAsset = getCharacterVoiceAsset(character);

  // Format the filename for display
  const displayName =
    displayCharacterName.length > 20
      ? displayCharacterName.substring(0, 17) + '...'
      : displayCharacterName;
  const assetCount = character.assets?.length ?? 1;

  return (
    <div className="character-card">
      {/* Image Preview */}
      <div className="character-card-image">
        <img src={imageSrc} alt={character.imageName} />
        {riggingEnabled && (
          <span
            className={`rig-badge ${rigComplete ? 'complete' : 'incomplete'}`}
          >
            {rigComplete ? 'Rigged' : 'Not Rigged'}
          </span>
        )}
      </div>

      {/* Info Section */}
      <div className="character-card-info">
        <div className="character-card-name" title={displayCharacterName}>
          {displayName}
        </div>

        <div className="character-card-meta">
          {assetCount} asset{assetCount === 1 ? '' : 's'}
          {riggingEnabled &&
            ` · ${character.joints.length} joints, ${character.bones.length} bones`}
        </div>

        {hasVoice && (
          <div className="character-card-voice">
            <GraphicEq style={{ fontSize: 14 }} />
            <span>
              {voiceAsset?.name || 'Voice reference attached'}
            </span>
          </div>
        )}

        {(character.identity?.gender ||
          character.identity?.species ||
          (character.identity?.personality ?? []).length > 0) && (
          <div className="character-card-identity">
            {character.identity?.gender && (
              <span className="character-card-age">
                Gender: {character.identity.gender}
              </span>
            )}
            {character.identity?.species && (
              <span className="character-card-age">
                Species: {character.identity.species}
              </span>
            )}
            {(character.identity?.personality ?? []).length > 0 && (
              <div className="character-card-personality">
                {(character.identity?.personality ?? []).map((trait) => (
                  <span key={trait} className="personality-chip">
                    {trait}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="character-card-actions">
          <button
            className="card-action-btn secondary"
            onClick={onEditDetails}
            title="Edit details"
          >
            <Edit style={{ fontSize: 14 }} />
            Edit
          </button>

          {riggingEnabled && (
            <button
              className="card-action-btn secondary"
              onClick={onEdit}
              title="Open Studio"
            >
              <Edit style={{ fontSize: 14 }} />
              Open Studio
            </button>
          )}

          {riggingEnabled && rigComplete && (
            <button
              className="card-action-btn secondary"
              onClick={onApplyAction}
              title="Apply Action"
            >
              <PlayArrow style={{ fontSize: 14 }} />
              Action
            </button>
          )}

          {riggingEnabled && rigComplete && onRecordAnimation && (
            <button
              className="card-action-btn secondary record"
              onClick={onRecordAnimation}
              title="Record Animation"
            >
              <FiberManualRecord style={{ fontSize: 14 }} />
              Record
            </button>
          )}

          {onVoice && (
            <button
              className="card-action-btn secondary"
              onClick={onVoice}
              title="Manage voice reference"
            >
              <GraphicEq style={{ fontSize: 14 }} />
              Voice
            </button>
          )}

          {riggingEnabled && (
            <button
              className="card-action-btn primary"
              onClick={onAddToCanvas}
              title="Add to Canvas"
            >
              <Add style={{ fontSize: 14 }} />
              Add
            </button>
          )}

          <button
            className="card-action-btn danger"
            onClick={onDelete}
            title="Delete"
          >
            <Delete style={{ fontSize: 14 }} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CharacterCard;
